import { zodResolver } from "@hookform/resolvers/zod";
import { GridRowId } from "@mui/x-data-grid";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useProdutoServicoDrawer } from "../../../../common/components/drawers_api/drawers/produto_servico_drawer";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { DropDownProdutoServicoDto } from "../../../../common/domain/models/dropdown_produto_servico";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import {
  useCreateDebounceHandler,
  useDebouncedCallback,
} from "../../../../common/hooks/use_debounced_callback";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { useUserAuthStore } from "../../../../stores/global/userAuth";
import { SaidaProduto } from "../../domain/models/saida_produtos_lista";
import { useSaidaProdutoFilters } from "../hooks/saida_produto/filter";
import { useSaidaProdutoPagination } from "../hooks/saida_produto/pagination";
import {
  SaidaProdutoForm,
  SaidaProdutoSchema,
} from "../schemas/Entrada_Produtos/saida_produtos";

type LoadEstoqueListaFuncState = {
  IdSaidaProduto?: number;
  IdSessaoMultipla?: string;
  page?: number;
  rowsPerPage?: number;
  FuncionarioPesquisa?: number;
  ProdutoPesquisa?: number;
  pesquisa?: string | null;
  centroDeCustoSaida?: number[];
  centroDeCustoDestino?: number[];
  centroDeCustoLista?: CentroCustoDropDown[] | undefined;
  dataSaidaDe?: Date | null;
  dataSaidaAte?: Date | null;
};
export const useSaidaProdutosListaViewModel = () => {
  const {
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
      getFuncionariosDropDownUseCase,
      getProdutoServicoDropDownUseCase,
    },
    reports: { getReportSaidaProdutoUseCase },
    controleEstoque: {
      getAllListagemSaidaUseCase,
      createSaidaPUseCase,
      updateSaidaPUseCase,
      deleteSaidaPUseCase,
      updateSaidaMultiPUseCase,
      createSaidaMultiPUseCase,
    },
    compras: { createServicoProdutoUseCase },
  } = useDependencyInjection();

  const { idUsuario } = useUserAuthStore();

  const { loadCentroCustoDropDown, loadProdutoServicosDropDown } =
    useDropDownLoader();

  const hookForm = useForm<SaidaProdutoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(SaidaProdutoSchema),
  });
  //#region Relatorio
  const [openReportModal, setOpenReportModal] = useState(false);
  const [reportViewerState, startReportViewerProcess] = useReportViewer();
  const [produtoServicoDrawerState, startProdutoServicoDrawer] =
    useProdutoServicoDrawer();
  //#endregion
  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [saidaModal, setSaidaModal] = useState<boolean>(false);
  const [saidaMultiModal, setSaidaMultiModal] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [saidaProdutoLista, setSaidaProdutoLista] = useState<SaidaProduto[]>(
    [],
  );
  const [produtoIdSessaoLista, setProdutoIdSessaoLista] = useState<
    SaidaProduto[]
  >([]);
  const [centroDeCustoLista, setCentroDeCustoLista] = useState<
    CentroCustoDropDown[]
  >([]);
  const [funcionariosLista, setFuncionariosLista] = useState<
    FuncionarioDropDown[]
  >([]);
  const [selectedSaidaProduto, setSelectedSaidaProduto] = useState<
    SaidaProduto | undefined
  >(undefined);
  const [idSaidaProduto, setIdSaidaProduto] = useState<number | undefined>(
    undefined,
  );
  const [produtosServicosDropDown, setProdutosServicosDropDown] =
    useState<DropDownProdutoServicoDto[]>();
  const [listaItens, setListaItens] = useState<SaidaProdutoForm[]>([]);
  const [selectedProdutosServicosQuery, setSelectedProdutosServicosQuery] =
    useState<DropDown[] | null>(null);
  const [selectedCentroCustoQuery, setSelectedCentroCustoQuery] = useState<
    CentroCustoDropDown[] | null
  >(null);
  const [selectedCentroCustoDestinoQuery, setSelectedCentroCustoDestinoQuery] =
    useState<CentroCustoDropDown[] | null>(null);
  const {
    pushErrorNotification,
    setCustomLoading,
    setPageSaving,
    pushInfoNotification,
    pushWarnNotification,
  } = usePageLoader();
  const debouncedRef = useCreateDebounceHandler(750);

  const filters = useSaidaProdutoFilters();
  const pagination = useSaidaProdutoPagination();

  // #region Funções de carregamento

  const loadEstoqueListaState = useCallback(
    async (funcState?: LoadEstoqueListaFuncState) => {
      try {
        const filterID_EntradaProduto = evaluateParamValue(
          funcState?.IdSaidaProduto,
          filters.IdSaidaProduto,
        );
        const filteredIdSessaoMultipla = funcState?.IdSessaoMultipla;
        const filteredFuncionarioPesquisa =
          funcState?.FuncionarioPesquisa ?? filters.FuncionarioPesquisa;
        const filteredProdutoPesquisa =
          funcState?.ProdutoPesquisa ?? filters.ProdutoPesquisa;
        const filteredCentroDeCusto =
          funcState?.centroDeCustoDestino ?? filters.centroDeCustoDestino;
        const filteredCentroDeCustoSaida =
          funcState?.centroDeCustoSaida ?? filters.centroDeCustoSaida;
        const filteredDataSaidaAte =
          funcState?.dataSaidaAte ?? filters.dataSaidaAte;
        const filteredDataSaidaDe =
          funcState?.dataSaidaDe ?? filters.dataSaidaDe;
        const filteredPesquisa = funcState?.pesquisa ?? filters.pesquisa;
        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage =
          funcState?.rowsPerPage ?? pagination.rowsPerPage;

        await getAllListagemSaidaUseCase.getAllListagemSaida(
          {
            IdSaidaProduto: filterID_EntradaProduto,
            centroDeCustoDestino: filteredCentroDeCusto,
            centroDeCustoSaida: filteredCentroDeCustoSaida,
            pesquisa: String(filteredPesquisa),
            dataSaidaAte: filteredDataSaidaAte,
            dataSaidaDe: filteredDataSaidaDe,
            FuncionarioPesquisa: filteredFuncionarioPesquisa,
            ProdutoPesquisa: filteredProdutoPesquisa,
            IdSessaoMultipla: filteredIdSessaoMultipla,
          },
          filteredPage,
          filteredRowsPerPage,
        );
        if (!filteredIdSessaoMultipla)
          setSaidaProdutoLista(getAllListagemSaidaUseCase.items);
        if (filteredIdSessaoMultipla)
          setProdutoIdSessaoLista(
            getAllListagemSaidaUseCase.items.filter(
              (x) => x.IdSessaoMultipla === filteredIdSessaoMultipla,
            ),
          );
        pagination.setRowsPerPage(getAllListagemSaidaUseCase.pageSize);
        pagination.setTotalItems(getAllListagemSaidaUseCase.totalItems);
      } catch (e: unknown) {
        throw new Error("Falha ao buscar saidas produtos!");
      }
    },
    [
      filters.FuncionarioPesquisa,
      filters.IdSaidaProduto,
      filters.ProdutoPesquisa,
      filters.centroDeCustoDestino,
      filters.centroDeCustoSaida,
      filters.dataSaidaAte,
      filters.dataSaidaDe,
      filters.pesquisa,
      getAllListagemSaidaUseCase,
      pagination,
    ],
  );

  const loadProdutosServicosDropdown = useCallback(
    async (id?: number) => {
      try {
        const results =
          await getProdutoServicoDropDownUseCase.getProdutoServicoDropDown("P");
        setProdutosServicosDropDown(results);
      } catch (e: unknown) {
        setProdutosServicosDropDown(undefined);
        throw new Error("Falha ao buscar produtos/serviços!");
      }
    },
    [getProdutoServicoDropDownUseCase],
  );

  const loadFuncionarioDropdown = useCallback(
    async (id?: number) => {
      try {
        const results =
          await getFuncionariosDropDownUseCase.getFuncionariosDropDown();
        setFuncionariosLista(results);
      } catch (e: unknown) {
        setFuncionariosLista([]);
        throw new Error("Falha ao buscar funcionários!");
      }
    },
    [getFuncionariosDropDownUseCase],
  );
  //#endregion

  // #region useEffect
  usePageStartBootstrap(async () => {
    try {
      const jobs = [];

      jobs.push(loadEstoqueListaState());
      await loadCentroCustoDropDown((list) => setCentroDeCustoLista(list));
      jobs.push(
        loadCentroCustoDropDown((list) =>
          useSaidaProdutoFilters.setState({ centroDeCustoLista: list }),
        ),
      );
      jobs.push(loadCentroCustoDropDown((list) => setCentroDeCustoLista(list)));
      jobs.push(loadProdutosServicosDropdown());
      jobs.push(loadFuncionarioDropdown());

      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      const jobs = [];

      setLoading(true);
      jobs.push(loadEstoqueListaState());
      await loadCentroCustoDropDown((list) => setCentroDeCustoLista(list));
      jobs.push(
        loadCentroCustoDropDown((list) =>
          useSaidaProdutoFilters.setState({ centroDeCustoLista: list }),
        ),
      );
      jobs.push(loadCentroCustoDropDown((list) => setCentroDeCustoLista(list)));
      jobs.push(loadProdutosServicosDropdown());
      jobs.push(loadFuncionarioDropdown());

      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao buscar dados da pagina!"),
      );
    } finally {
      setLoading(false);
    }
  }, [
    loadCentroCustoDropDown,
    loadEstoqueListaState,
    loadFuncionarioDropdown,
    loadProdutosServicosDropdown,
    pushErrorNotification,
  ]);
  //#endregion

  // #region Eventos
  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        pagination.setPage(page + 1);
        await loadEstoqueListaState({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadEstoqueListaState, pagination, pushErrorNotification],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        pagination.setPage(1);
        pagination.setRowsPerPage(rowsPerPage);

        await loadEstoqueListaState({ page: 1, rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadEstoqueListaState, pagination, pushErrorNotification],
  );

  const handleCadastroSaidaOpenState = useCallback(
    (id: number | null, saidaModal: boolean) => {
      if (id) {
        setSelectedSaidaProduto(
          saidaProdutoLista.find((x) => x.IdSaidaProduto === id),
        );
        setSaidaModal(saidaModal);
      } else {
        setSelectedSaidaProduto(undefined);
        setSaidaModal(saidaModal);
      }
    },
    [saidaProdutoLista],
  );

  const handleCadastroSaidaMultiOpenState = useCallback(
    (saidaModal: boolean) => {
      setSelectedProdutosServicosQuery(null);
      setProdutoIdSessaoLista([]);
      if (saidaModal) setSaidaMultiModal(saidaModal);
      else setSaidaMultiModal(false);
    },
    [],
  );

  const handleDeleteModalOpenState = useCallback(
    (Id: number | null, openState: boolean) => {
      if (openState) {
        if (Id && saidaProdutoLista) {
          setSelectedSaidaProduto(
            saidaProdutoLista.find((x) => x.IdSaidaProduto === Id),
          );
        } else {
          setSelectedSaidaProduto(undefined);
        }
      }
      setDeleteModal(openState);
    },
    [saidaProdutoLista],
  );

  const handleDeleteConfirma = useCallback(async () => {
    if (selectedSaidaProduto) {
      try {
        setSaving(true);
        await deleteSaidaPUseCase.deleteSaidaP(
          selectedSaidaProduto.IdSaidaProduto,
        );
        await loadEstoqueListaState();
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao excluir produto!"),
        );
      } finally {
        setSaving(false);
        setDeleteModal(false);
      }
    }
  }, [
    deleteSaidaPUseCase,
    loadEstoqueListaState,
    pushErrorNotification,
    selectedSaidaProduto,
  ]);

  const handleSaidaProdutoSubmit = useCallback(
    async (form: SaidaProdutoForm) => {
      try {
        if (selectedSaidaProduto) {
          await updateSaidaPUseCase.updateSaidaP(
            selectedSaidaProduto.IdSaidaProduto,
            form.data ?? null,
            form.id_centro_custo ?? null,
            form.id_centro_custo_destino ?? null,
            form.id_produto_servico ?? null,
            form.nro_nota_fiscal ?? null,
            form.quantidade ?? null,
            form.id_fornecedor ?? null,
            form.id_funcionario ?? null,
            form.nr_nota_fiscal ?? null,
            form.id_sessao_multipla ?? null,
            false,
            form.valor_unitario ?? null,
            form.localAplicacao ?? null,
          );
          pushInfoNotification("Sucesso", "Produto atualizado com sucesso!", {
            isAutoFinish: true,
          });
          setSaidaModal(false);
          await loadEstoqueListaState();
        } else {
          var id = await createSaidaPUseCase.createSaidaP(
            form.data ?? null,
            form.id_centro_custo ?? null,
            form.id_centro_custo_destino ?? null,
            form.id_produto_servico ?? null,
            form.nro_nota_fiscal ?? null,
            form.quantidade ?? null,
            form.id_fornecedor ?? null,
            form.id_funcionario ?? null,
            form.nr_nota_fiscal ?? null,
            form.id_sessao_multipla ?? null,
            false,
            form.valor_unitario ?? null,
            form.localAplicacao ?? null,
          );
          pushInfoNotification("Sucesso", "Produto criado com sucesso!", {
            isAutoFinish: true,
          });
          setIdSaidaProduto(id);
          setSaidaModal(false);
          await loadEstoqueListaState();
        }
      } catch (e) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao criar/atualizar Saída Produtos!"),
        );
      } finally {
        setSaving(false);
      }
    },
    [
      createSaidaPUseCase,
      loadEstoqueListaState,
      pushErrorNotification,
      pushInfoNotification,
      selectedSaidaProduto,
      updateSaidaPUseCase,
    ],
  );

  const handleSaidaProdutoMultiSubmit = useCallback(
    async (form: SaidaProdutoForm) => {
      try {
        const selectedProdutoServicoIds =
          selectedProdutosServicosQuery?.map((x) => x.id) ?? null;
        const IdSessaoMultipla =
          await createSaidaMultiPUseCase.createSaidaMultiP(
            form.data ?? null,
            form.id_centro_custo ?? null,
            form.id_centro_custo_destino ?? null,
            selectedProdutoServicoIds ?? null,
            form.nro_nota_fiscal ?? null,
            form.quantidade ?? null,
            form.id_fornecedor ?? null,
            form.id_funcionario ?? null,
            form.nr_nota_fiscal ?? null,
            form.id_sessao_multipla ?? null,
            false,
            form.valor_unitario ?? null,
            form.localAplicacao ?? null,
          );
        pushInfoNotification("Sucesso", "Produtos criado com sucesso!", {
          isAutoFinish: true,
        });

        await loadEstoqueListaState({
          IdSessaoMultipla: String(IdSessaoMultipla),
        });
      } catch (e) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao criar/atualizar Produtos!"),
        );
      } finally {
        setSaving(false);
      }
    },
    [
      createSaidaMultiPUseCase,
      loadEstoqueListaState,
      pushErrorNotification,
      pushInfoNotification,
      selectedProdutosServicosQuery,
    ],
  );

  const handleUpdateMutiTodos = async () => {
    try {
      setSaving(true);
      const payload = produtoIdSessaoLista;
      if (payload.length === 0)
        return pushWarnNotification(
          "Atenção",
          "Nenhum produto selecionado para atualização!",
        );

      await updateSaidaMultiPUseCase.updateSaidaMultiP(payload);

      pushInfoNotification("Sucesso", "Produtos atualizados com sucesso!", {
        isAutoFinish: true,
      });
      setProdutoIdSessaoLista([]);
      await loadEstoqueListaState();
    } catch (e) {
      pushErrorNotification("Erro", "Falha ao atualizar os Produtos.");
    } finally {
      if (produtoIdSessaoLista.length > 0) {
        setSelectedProdutosServicosQuery(null);
        setSaidaMultiModal(false);
        setSaidaModal(false);
      }
      setSaving(false);
    }
  };

  const handleProdutosServicosQueryChange = useCallback(
    (items: DropDown[] | null) => {
      setSelectedProdutosServicosQuery(items);
    },
    [],
  );

  const handleCentroCustoChange = (
    novoIdCentroCusto: number | null,
    idLinhaGrid: GridRowId,
  ) => {
    setProdutoIdSessaoLista((listaAtual) =>
      listaAtual.map((item) =>
        item.IdSaidaProduto === idLinhaGrid
          ? { ...item, IdCentroCusto: novoIdCentroCusto }
          : item,
      ),
    );
  };
  const handleCentroCustoDestinoChange = (
    novoIdCentroCusto: number | null,
    idLinhaGrid: GridRowId,
  ) => {
    setProdutoIdSessaoLista((listaAtual) =>
      listaAtual.map((item) =>
        item.IdSaidaProduto === idLinhaGrid
          ? { ...item, IdCentroCustoDestino: novoIdCentroCusto }
          : item,
      ),
    );
  };
  const handleLocalAplicacaoChange = (
    novoLocalAplicacao: string | null,
    idLinhaGrid: GridRowId,
  ) => {
    setProdutoIdSessaoLista((listaAtual) =>
      listaAtual.map((item) =>
        item.IdSaidaProduto === idLinhaGrid
          ? { ...item, LocalAplicacao: novoLocalAplicacao }
          : item,
      ),
    );
  };
  const handleQuantidadeChange = (
    novaQtd: number | null,
    idLinhaGrid: GridRowId,
  ) => {
    setProdutoIdSessaoLista((listaAtual) =>
      listaAtual.map((item) =>
        item.IdSaidaProduto === idLinhaGrid
          ? { ...item, Quantidade: novaQtd }
          : item,
      ),
    );
  };
  const handleFuncionarioMChange = (
    novoIdFuncionario: number | null,
    idLinhaGrid: GridRowId,
  ) => {
    setProdutoIdSessaoLista((listaAtual) =>
      listaAtual.map((item) =>
        item.IdSaidaProduto === idLinhaGrid
          ? { ...item, IdFuncionario: novoIdFuncionario }
          : item,
      ),
    );
  };
  const handleFornecedorMChange = (
    novoIdFornecedor: number | null,
    idLinhaGrid: GridRowId,
  ) => {
    setProdutoIdSessaoLista((listaAtual) =>
      listaAtual.map((item) =>
        item.IdSaidaProduto === idLinhaGrid
          ? { ...item, IdFornecedor: novoIdFornecedor }
          : item,
      ),
    );
  };

  const handleValorChange = (
    novoValor: string | null,
    IdSaidaProduto: number,
  ) => {
    setProdutoIdSessaoLista((prevState) => {
      return prevState.map((item): SaidaProduto => {
        if (item.IdSaidaProduto === IdSaidaProduto) {
          return {
            ...item,
            ValorUnitario: novoValor !== null ? Number(novoValor) : null,
          };
        }
        return item;
      });
    });
  };

  const handleDataChange = (novaData: Date | null, IdSaidaProduto: number) => {
    setProdutoIdSessaoLista((prevState) => {
      return prevState.map((item): SaidaProduto => {
        if (item.IdSaidaProduto === IdSaidaProduto) {
          return {
            ...item,
            Data: novaData ? novaData : new Date(),
          };
        }
        return item;
      });
    });
  };

  //#endregion

  //#region Filtros
  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      filters.clearFilters();
      pagination.setPage(1);
      await loadEstoqueListaState();
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao buscar produtos!"),
      );
    } finally {
      setLoading(false);
    }
  }, [filters, loadEstoqueListaState, pagination, pushErrorNotification]);

  const handlePesquisaChange = useDebouncedCallback(
    debouncedRef,
    (newValue: string | null) => {
      useSaidaProdutoFilters.getState().setPesquisa(newValue);

      if (newValue) {
        return async () => {
          try {
            setLoading(true);
            useSaidaProdutoPagination.getState().setPage(1);
            await loadEstoqueListaState({ pesquisa: newValue, page: 1 });
          } catch (e: unknown) {
            pushErrorNotification(
              "Erro",
              processError(e, "Falha ao carregar produtos!"),
            );
          } finally {
            setLoading(false);
          }
        };
      } else {
        return async () => {
          try {
            setLoading(true);
            useSaidaProdutoPagination.getState().setPage(1);
            await loadEstoqueListaState();
          } catch (e: unknown) {
            pushErrorNotification(
              "Erro",
              processError(e, "Falha ao carregar produtos!"),
            );
          } finally {
            setLoading(false);
          }
        };
      }
    },
    [loadEstoqueListaState, pushErrorNotification, setLoading],
  );

  const handleFuncionarioChange = useDebouncedCallback(
    debouncedRef,
    async (newValue: number) => {
      useSaidaProdutoFilters.getState().setFuncionarioPesquisa(newValue);

      try {
        setLoading(true);
        useSaidaProdutoPagination.getState().setPage(1);
        await loadEstoqueListaState({
          FuncionarioPesquisa: newValue,
          page: 1,
        } as any);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar !"));
      } finally {
        setLoading(false);
      }

      return () => {};
    },
    [pushErrorNotification, loadEstoqueListaState],
  );

  const handleProdutorChange = useDebouncedCallback(
    debouncedRef,
    async (newValue: number) => {
      useSaidaProdutoFilters.getState().setProdutoPesquisa(newValue);

      try {
        setLoading(true);
        useSaidaProdutoPagination.getState().setPage(1);
        await loadEstoqueListaState({
          ProdutoPesquisa: newValue,
          page: 1,
        } as any);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar !"));
      } finally {
        setLoading(false);
      }

      return () => {};
    },
    [pushErrorNotification, loadEstoqueListaState],
  );

  const handleCentroDeCustoSaidaChange = useDebouncedCallback(
    debouncedRef,
    (newValue: number[]) => {
      useSaidaProdutoFilters.getState().setCentroDeCustoSaida(newValue);
      return async () => {
        try {
          setLoading(true);
          useSaidaProdutoPagination.getState().setPage(1);
          await loadEstoqueListaState({
            centroDeCustoSaida: newValue,
            page: 1,
          });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao carregar !"));
        } finally {
          setLoading(false);
        }
      };
    },
    [],
  );

  const handleCentroDeCustoDestinoChange = useDebouncedCallback(
    debouncedRef,
    (newValue: number[]) => {
      useSaidaProdutoFilters.getState().setCentroDeCustoDestino(newValue);
      return async () => {
        try {
          setLoading(true);
          useSaidaProdutoPagination.getState().setPage(1);
          await loadEstoqueListaState({
            centroDeCustoDestino: newValue,
            page: 1,
          });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao carregar !"));
        } finally {
          setLoading(false);
        }
      };
    },
    [],
  );

  const handleDataSaidaRangeChange = useDebouncedCallback(
    debouncedRef,
    (dataSaidaDe: Date | null, dataSaidaAte: Date | null) => {
      return async () => {
        try {
          if (
            dataSaidaDe &&
            dataSaidaAte &&
            dataSaidaDe !== null &&
            dataSaidaAte !== null
          ) {
            filters.setFilters({ dataSaidaDe });
            filters.setFilters({ dataSaidaAte });
          } else {
            await loadEstoqueListaState();
          }
          setLoading(true);
          pagination.setPage(1);
          await loadEstoqueListaState({ dataSaidaDe, dataSaidaAte, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Error",
            processError(e, "Falha ao buscar produtos!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [filters],
  );

  //#endregion

  //#region Relatoria
  const handleReportModalOpenState = useCallback((openState: boolean) => {
    setOpenReportModal(openState);
  }, []);

  const handleCentroCustoQueryChange = useCallback(
    (items: CentroCustoDropDown[] | null) => {
      setSelectedCentroCustoQuery(items);
    },
    [],
  );

  const handleCentroCustoDestinoQueryChange = useCallback(
    (items: CentroCustoDropDown[] | null) => {
      setSelectedCentroCustoDestinoQuery(items);
    },
    [],
  );

  const handleSubmitReport = useCallback(
    async (form: SaidaProdutoForm) => {
      try {
        setSaving(true);
        const selectedCentroCustods =
          selectedCentroCustoQuery?.map((x) => x.id) ?? [];
        const selectedCentroCustoDestinods =
          selectedCentroCustoDestinoQuery?.map((x) => x.id) ?? [];
        await startReportViewerProcess({
          fetchCallback: async (requiredMimeType) =>
            await getReportSaidaProdutoUseCase.getReportSaidaProduto(
              requiredMimeType,
              idUsuario,
              form.id_funcionario,
              form.id_produto_servico,
              selectedCentroCustods,
              selectedCentroCustoDestinods,
              form.data,
              form.dataSaida,
            ),
          closeReportModalCallback: () => {
            setOpenReportModal(false);
          },
        });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao gerar relatorio"),
        );
      } finally {
        setSaving(false);
      }
    },
    [
      getReportSaidaProdutoUseCase,
      pushErrorNotification,
      selectedCentroCustoDestinoQuery,
      selectedCentroCustoQuery,
      startReportViewerProcess,
    ],
  );

  const handleOpenChangeHistory = useCallback(
    (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
      if (idTabela && tipoTabela) {
        changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
      }
    },
    [changeHistory],
  );

  const handleNewProdutoServico = useCallback(() => {
    startProdutoServicoDrawer(async (form) => {
      try {
        setSaving(true);
        setPageSaving(true);

        const idProdutoServico =
          await createServicoProdutoUseCase.createServicoProduto(
            form.ativo,
            form.produtoServico,
            form.tipo,
            form.unidade,
            form.norma,
          );

        await loadProdutoServicosDropDown((dt) => {
          const createdProdutoServico = dt.find(
            (x) => x.idProdutoServico === idProdutoServico,
          );
          if (createdProdutoServico) {
            setSelectedProdutosServicosQuery((prev) =>
              prev !== null
                ? [
                    ...prev,
                    {
                      id: createdProdutoServico.idProdutoServico,
                      title: createdProdutoServico.descricao,
                    },
                  ]
                : null,
            );
          }
          setProdutosServicosDropDown(
            dt.map((x) => ({ id: x.idProdutoServico, title: x.descricao })),
          );
        }, {});

        pushInfoNotification("Sucesso", "Registro salvo com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao salvar requisição de produto/serviço!"),
        );
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    });
  }, [
    createServicoProdutoUseCase,
    startProdutoServicoDrawer,
    loadProdutoServicosDropDown,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  //#endregion

  return {
    loading,
    saving,
    errorMessage,
    saidaProdutoLista,
    selectedSaidaProduto,
    reportViewerState,
    produtoServicoDrawerState,
    pagination,
    filters,
    saidaModal,
    changeHistory,
    centroDeCustoLista,
    hookForm,
    deleteModal,
    produtosServicosDropDown,
    selectedProdutosServicosQuery,
    saidaMultiModal,
    listaItens,
    funcionariosLista,
    produtoIdSessaoLista,
    selectedCentroCustoQuery,
    openReportModal,
    selectedCentroCustoDestinoQuery,
    handleCentroCustoDestinoQueryChange,
    handleReportModalOpenState,
    handleSubmitReport,
    handleCentroCustoQueryChange,
    handleCentroDeCustoSaidaChange,
    handleCentroDeCustoDestinoChange,
    handleUpdateMutiTodos,
    handleCentroCustoChange,
    handleValorChange,
    handleFornecedorMChange,
    handleQuantidadeChange,
    handleSaidaProdutoMultiSubmit,
    setSaidaMultiModal,
    handleCadastroSaidaOpenState,
    setSaidaModal,
    handleSaidaProdutoSubmit,
    handleReload,
    handleRowsPerPageChange,
    handlePageChange,
    handleDeleteConfirma,
    handleDeleteModalOpenState,
    handleClearFilters,
    handleFuncionarioChange,
    handleDataSaidaRangeChange,
    handlePesquisaChange,
    handleProdutorChange,
    handleOpenChangeHistory,
    handleProdutosServicosQueryChange,
    handleCadastroSaidaMultiOpenState,
    handleCentroCustoDestinoChange,
    handleFuncionarioMChange,
    handleDataChange,
    handleNewProdutoServico,
    handleLocalAplicacaoChange,
  };
};
